import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Icon,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import ReactApexChart from "react-apexcharts";
import Card from "components/card/Card";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import referralsService from "services/referralsService";
import { formatNumber } from "utils/format";
import {
  MdBarChart,
  MdCheckCircle,
  MdGroups,
  MdPeople,
  MdPendingActions,
  MdPersonSearch,
  MdTrendingUp,
  MdWarning,
} from "react-icons/md";

const emptySummary = {
  totals: {
    total_referrals: 0,
    processed: 0,
    pending: 0,
    failed: 0,
    referred_students: 0,
    conversion_rate: 0,
    processing_rate: 0,
  },
  status_counts: {},
  monthly_counts: [],
  top_referrals: [],
};

export default function IReferalsReports() {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const cardBg = useColorModeValue("white", "navy.800");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.100");
  const rowHoverBg = useColorModeValue("secondaryGray.50", "whiteAlpha.50");
  const brandColor = useColorModeValue("brand.500", "white");
  const chartTextColor = useColorModeValue("#2D3748", "#FFFFFF");
  const gridColor = useColorModeValue("#E2E8F0", "#2D3748");

  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await referralsService.getReferralReportSummary();
        if (result.success && result.data) {
          setSummary(result.data);
        } else {
          setError(result.error || "Failed to load referral reports.");
        }
      } catch (err) {
        console.error("Referral report summary error:", err);
        setError("Failed to load referral reports.");
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  const totals = summary?.totals || emptySummary.totals;
  const statusCounts = summary?.status_counts || {};
  const monthlyCounts = summary?.monthly_counts || [];
  const topReferrals = summary?.top_referrals || [];

  const chartTheme = useMemo(
    () => ({
      foreColor: chartTextColor,
      toolbar: { show: false },
    }),
    [chartTextColor]
  );

  const statusSeries = [
    statusCounts.processed || 0,
    statusCounts.pending || 0,
    statusCounts.failed || 0,
  ];

  const statusOptions = {
    chart: chartTheme,
    labels: ["Processed", "Pending", "Failed"],
    colors: ["#16A34A", "#F59E0B", "#DC2626"],
    legend: {
      position: "bottom",
      labels: { colors: chartTextColor },
    },
    dataLabels: {
      enabled: true,
      formatter: (value) => `${value.toFixed(0)}%`,
    },
    stroke: { width: 0 },
  };

  const monthlyOptions = {
    chart: chartTheme,
    colors: ["#2563EB"],
    xaxis: {
      categories: monthlyCounts.map((item) => item.month),
      labels: { style: { colors: chartTextColor } },
    },
    yaxis: {
      labels: { style: { colors: chartTextColor } },
    },
    grid: { borderColor: gridColor },
    plotOptions: {
      bar: {
        borderRadius: 5,
        columnWidth: "48%",
      },
    },
    dataLabels: { enabled: false },
  };

  const topReferralOptions = {
    chart: chartTheme,
    colors: ["#0891B2"],
    xaxis: {
      categories: topReferrals.map((item) => item.name || "Unknown"),
      labels: {
        style: { colors: chartTextColor },
        trim: true,
      },
    },
    yaxis: {
      labels: { style: { colors: chartTextColor } },
    },
    grid: { borderColor: gridColor },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 5,
      },
    },
    dataLabels: { enabled: false },
  };

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="420px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error" borderRadius="12px">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex direction="column" gap="20px">
        <Box>
          <Text color={textColor} fontSize="2xl" fontWeight="700">
            Referral Reports
          </Text>
          <Text color={textColorSecondary} fontSize="sm" mt="4px">
            Performance summary across all referral records and referred student applications.
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap="20px">
          <MiniStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={<Icon w="32px" h="32px" as={MdPeople} color={brandColor} />}
              />
            }
            name="Total Referrals"
            value={formatNumber(totals.total_referrals || 0)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={<Icon w="32px" h="32px" as={MdGroups} color="cyan.500" />}
              />
            }
            name="Referred Students"
            value={formatNumber(totals.referred_students || 0)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={<Icon w="32px" h="32px" as={MdTrendingUp} color="green.500" />}
              />
            }
            name="Student Conversion"
            value={`${totals.conversion_rate || 0}%`}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={<Icon w="32px" h="32px" as={MdCheckCircle} color="green.500" />}
              />
            }
            name="Processing Rate"
            value={`${totals.processing_rate || 0}%`}
          />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 3 }} gap="20px">
          <MiniStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={<Icon w="32px" h="32px" as={MdCheckCircle} color="green.500" />}
              />
            }
            name="Processed"
            value={formatNumber(totals.processed || 0)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={<Icon w="32px" h="32px" as={MdPendingActions} color="orange.500" />}
              />
            }
            name="Pending"
            value={formatNumber(totals.pending || 0)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={<Icon w="32px" h="32px" as={MdWarning} color="red.500" />}
              />
            }
            name="Failed"
            value={formatNumber(totals.failed || 0)}
          />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, xl: 2 }} gap="20px">
          <Card p="24px" bg={cardBg}>
            <Flex align="center" gap="10px" mb="18px">
              <Icon as={MdBarChart} color={brandColor} boxSize="22px" />
              <Text color={textColor} fontSize="lg" fontWeight="700">
                Referral Status
              </Text>
            </Flex>
            <Box minH="300px">
              {statusSeries.some((value) => value > 0) ? (
                <ReactApexChart
                  options={statusOptions}
                  series={statusSeries}
                  type="donut"
                  width="100%"
                  height={300}
                />
              ) : (
                <Flex minH="260px" align="center" justify="center">
                  <Text color={textColorSecondary}>No referral status data yet.</Text>
                </Flex>
              )}
            </Box>
          </Card>

          <Card p="24px" bg={cardBg}>
            <Flex align="center" gap="10px" mb="18px">
              <Icon as={MdTrendingUp} color={brandColor} boxSize="22px" />
              <Text color={textColor} fontSize="lg" fontWeight="700">
                Referrals by Month
              </Text>
            </Flex>
            <Box minH="300px">
              {monthlyCounts.length > 0 ? (
                <ReactApexChart
                  options={monthlyOptions}
                  series={[{ name: "Referrals", data: monthlyCounts.map((item) => item.count) }]}
                  type="bar"
                  width="100%"
                  height={300}
                />
              ) : (
                <Flex minH="260px" align="center" justify="center">
                  <Text color={textColorSecondary}>No monthly referral data yet.</Text>
                </Flex>
              )}
            </Box>
          </Card>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, xl: 2 }} gap="20px">
          <Card p="24px" bg={cardBg}>
            <Flex align="center" gap="10px" mb="18px">
              <Icon as={MdPersonSearch} color={brandColor} boxSize="22px" />
              <Text color={textColor} fontSize="lg" fontWeight="700">
                Top Referrers
              </Text>
            </Flex>
            <Box minH="340px">
              {topReferrals.length > 0 ? (
                <ReactApexChart
                  options={topReferralOptions}
                  series={[
                    {
                      name: "Students",
                      data: topReferrals.map((item) => item.referred_students_count || 0),
                    },
                  ]}
                  type="bar"
                  width="100%"
                  height={340}
                />
              ) : (
                <Flex minH="300px" align="center" justify="center">
                  <Text color={textColorSecondary}>No referred students yet.</Text>
                </Flex>
              )}
            </Box>
          </Card>

          <Card p="24px" bg={cardBg}>
            <Text color={textColor} fontSize="lg" fontWeight="700" mb="18px">
              Top Referrers Table
            </Text>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th color={textColorSecondary}>Name</Th>
                    <Th color={textColorSecondary}>Code</Th>
                    <Th color={textColorSecondary}>Phone</Th>
                    <Th color={textColorSecondary} isNumeric>Students</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {topReferrals.length > 0 ? (
                    topReferrals.map((referral) => (
                      <Tr key={referral.id} _hover={{ bg: rowHoverBg }}>
                        <Td borderColor={borderColor}>
                          <Text color={textColor} fontWeight="600" fontSize="sm">
                            {referral.name || "-"}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text color={textColorSecondary} fontSize="sm" fontFamily="mono">
                            {referral.referral_code || "-"}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text color={textColorSecondary} fontSize="sm">
                            {referral.phone_number || "-"}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor} isNumeric>
                          <Text color={textColor} fontSize="sm" fontWeight="700">
                            {formatNumber(referral.referred_students_count || 0)}
                          </Text>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={4} textAlign="center" py="24px" borderColor={borderColor}>
                        <Text color={textColorSecondary}>No referrers found.</Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Card>
        </SimpleGrid>
      </Flex>
    </Box>
  );
}
